// v4 port of v3 RenderHtml (components/common/renderHtml) — same pipeline
// (html-react-parser + <math> block extraction + AIImage pending branch +
// TextHighlighter hook + replaceItem passthrough; RenderHtml.scss recomposed
// as utilities) PLUS built-in post-mount math typesetting: v3 made every
// consumer call renderMathInElement themselves; here the component owns it.
import DOMPurify from 'dompurify';
import parse, { Element, type DOMNode, type HTMLReactParserOptions } from 'html-react-parser';
import { useEffect, useMemo, useRef } from 'react';

import { AIImage } from '@/components/ap/renderHtml/aiImage/AIImage';
import { usePendingImages } from '@/components/ap/renderHtml/aiImage/PendingImagesContext';
import {
  TextHighlighterInitializer,
  type HighlightOptions,
} from '@/components/ap/renderHtml/TextHighlighterInitializer';
import { neutralizeAuthoredInk, resolveSurfaceColor, restoreAuthoredInk } from '@/lib/authoredInk';
import { cn } from '@/lib/cn';
import { renderMathInElement } from '@/lib/commonHelpers';
import { selectResolvedTheme, useThemeStore } from '@/lib/store/themeStore';

/** Consumer replace hook. Recurse children with the passed `options` (the
 *  host's own parser options) so keyed <math-data> reinjection survives
 *  inside replaced subtrees (J21-M01). */
export type APRenderHtmlReplace = (
  node: DOMNode,
  index: number,
  options: HTMLReactParserOptions
) => ReturnType<NonNullable<HTMLReactParserOptions['replace']>>;

export interface APRenderHtmlProps {
  text: string;
  /** v3 `truncate` mode — single 40px-line clamp with ellipsis. */
  truncate?: boolean;
  /**
   * DOMPurify the source before parsing. Default false = v3 RenderHtml parity
   * (BE-sanitized author content is trusted). Turn on for end-user-authored
   * strings (feedback messages, announcement previews).
   */
  sanitize?: boolean;
  /** Post-mount MathJax + Wiris typesetting. Default true. */
  math?: boolean;
  /** Forwarded to renderMathInElement — v3 consumers pass true everywhere. */
  renderUsingWiris?: boolean;
  /** Container element — use 'span' inside phrasing-only parents (buttons). */
  as?: 'div' | 'span';
  className?: string;
  highlightOptions?: HighlightOptions;
  replaceItem?: APRenderHtmlReplace;
  'data-testid'?: string;
}

// v3 replaceMathMLWithSpanDataAttribute, verbatim: lift raw <math> blocks out
// before parsing (html-react-parser mangles MathML namespaces) and reinject
// them untouched via dangerouslySetInnerHTML so MathJax/Wiris see them as-is.
const extractMathBlocks = (html: string) => {
  const mathMap: Record<string, string> = {};
  let index = 0;

  const processedHtml = html.replace(/<math[\s\S]*?<\/math>/g, (match) => {
    const key = `MATH_${index++}`;
    mathMap[key] = match;
    return `<math-data data-math-key="${key}"></math-data>`;
  });

  return { processedHtml, mathMap };
};

const BASE_CLASSES =
  "block whitespace-pre-wrap [&_p:empty]:before:inline-block [&_p:empty]:before:content-['_']";

// v3 .show-html
const SHOW_CLASSES =
  '[&>p]:my-0 [&_figure]:m-0 [&_img:not(.Wirisformula)]:h-auto [&_img:not(.Wirisformula)]:max-w-full [&_img:not(.Wirisformula)]:cursor-zoom-in [&_img.Wirisformula]:max-w-none';

// v3 .render-html — pre-wrap won over nowrap on the container (!important),
// direct-child <p> kept nowrap; heights rem-based so root font scaling holds.
const TRUNCATE_CLASSES =
  'h-10 w-full overflow-hidden text-ellipsis [&>p]:h-10 [&>p]:w-full [&>p]:overflow-hidden [&>p]:text-ellipsis [&>p]:whitespace-nowrap';

export const APRenderHtml = ({
  text,
  truncate = false,
  sanitize = false,
  math = true,
  renderUsingWiris = true,
  as: Container = 'div',
  className,
  highlightOptions,
  replaceItem,
  'data-testid': testId,
}: APRenderHtmlProps) => {
  // Intersection so the one ref satisfies both container flavors.
  const containerRef = useRef<HTMLDivElement & HTMLSpanElement>(null);
  const pendingImages = usePendingImages();
  const resolvedTheme = useThemeStore(selectResolvedTheme);

  const processed = useMemo(() => {
    if (!text) return null;
    return extractMathBlocks(sanitize ? DOMPurify.sanitize(text) : text);
  }, [text, sanitize]);

  const processedHtml = processed?.processedHtml ?? '';

  // Typeset after the parsed DOM commits. Keyed on the SOURCE text, not
  // processedHtml: extractMathBlocks reduces math-only content to an
  // unchanging <math-data> placeholder, so an in-place formula edit changes
  // text and mathMap but not processedHtml — and the effect must re-fire
  // for the new formula (RDS cell edits stayed raw MathML without this).
  // Re-renders without a text change still never re-typeset.
  useEffect(() => {
    if (!math || !processedHtml || !containerRef.current) return;
    void renderMathInElement(containerRef.current, renderUsingWiris);
  }, [math, text, processedHtml, renderUsingWiris]);

  // J22-B03: once content commits, contrast-check authored inline colours
  // against the theme surface — near-invisible inks on non-light themes lose
  // their inline colour (inherit the token foreground); originals restore
  // when light resumes. `.force-light` panes (print/export paper) are never
  // touched. Re-runs on content change (processedHtml) and theme flips.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !processedHtml || el.closest('.force-light')) return;
    if (resolvedTheme === 'light') {
      restoreAuthoredInk(el);
    } else {
      neutralizeAuthoredInk(el, resolveSurfaceColor(el));
    }
  }, [resolvedTheme, processedHtml]);

  if (!processed) return null;
  const { mathMap } = processed;

  const options: HTMLReactParserOptions = {
    replace(node: DOMNode, index: number) {
      if (node instanceof Element && node.name === 'math-data' && node.attribs['data-math-key']) {
        const mathMarkup = mathMap[node.attribs['data-math-key']];
        if (!mathMarkup) return; // fallback — shouldn't normally happen
        return <span dangerouslySetInnerHTML={{ __html: mathMarkup }} />;
      }

      // Pending AI-generated images — polling loader (active only inside a
      // PendingImagesProvider, i.e. AI generate results).
      if (
        pendingImages &&
        node instanceof Element &&
        node.name === 'img' &&
        node.attribs?.src &&
        pendingImages.pendingUrls.has(node.attribs.src)
      ) {
        const imgSrc = node.attribs.src;
        return (
          <AIImage
            src={imgSrc}
            alt={node.attribs.alt || 'Generated image'}
            onLoaded={() => pendingImages.onLoaded(imgSrc)}
          />
        );
      }

      if (typeof replaceItem === 'function') {
        return replaceItem(node, index, options);
      }

      return undefined;
    },
  };

  return (
    <>
      {highlightOptions && (
        <TextHighlighterInitializer htmlRef={containerRef} highlightOptions={highlightOptions} />
      )}
      <Container
        ref={containerRef}
        data-testid={testId}
        // The literal v3 `show-html` class is a cross-feature CONTRACT, not
        // styling: zoom targets (`.show-html img`, ZoomableImageLayer),
        // highlight offset origin + valid-container gate (textSelectionHelper),
        // translate/dictionary gates. v3 emitted it on every non-truncated
        // render — mirrored here (IZ2). The class alone is inert: highlight
        // capture mounts only via highlightOptions (scope context + rail tool).
        className={cn(
          BASE_CLASSES,
          truncate ? TRUNCATE_CLASSES : SHOW_CLASSES,
          !truncate && 'show-html',
          className
        )}
      >
        {parse(processedHtml, options)}
      </Container>
    </>
  );
};
